import React from "react";
import { ArticleIcon, CheckSmall, IconCheckCircle, IconContract, NewTimerIcon, PrinterIcon, SolarPowerIcon, TimerIcon } from "../../../../assests/icons/Icons";
import Contract from "./contract";
import Payment from "./Payment";
import PaymentSuccess from "./Paymentsuccess";
import { useAppDispatch, useAppSelector } from "../../../../ReduxTool/store/hooks";
import { getCurrencyLabel, projectInstallationDays, formatEnergyGeneration, formatMoney } from "../../../../Utils/commonFunctions";
import { getProposalFromEpc } from "../../../../ReduxTool/Slice/Consumer/Api";
import { setSeeProposal } from "../../../../ReduxTool/Slice/Consumer/ConsumerReducers";
import { PVNXT_MODE, SUPPORT_EMAIL } from "../../../../Utils/Const";
import { getInstallationModeContent } from "../../../../Utils/Const";

/* ─── Top stepper (Roof Confirmation → Review & submit) ─── */

export type ProposalStep = 1 | 2 | 3 | 4;

/* ─── Top stepper (line + label, no circles) ─────────────── */

interface StepperProps {
    currentStep: ProposalStep;
    completedStep: number;
    onStepClick?: (step: ProposalStep) => void;
}

const stepLabels = ["Proposal", "Contract", "Payment"];

export const TopStepper = ({ currentStep, completedStep, onStepClick }: StepperProps) => (
    <div className="flex w-full bg-white px-5 pt-3 pb-2 gap-2">
        {stepLabels.map((label, index) => {
            const step = (index + 1) as ProposalStep
            const isActive = step === currentStep;
            const isCompleted = step <= completedStep;
            const isClickable = step <= completedStep;

            return (
                <div key={label} className="flex flex-col gap-2 flex-1 cursor-pointer" onClick={() => isClickable && step !== currentStep && onStepClick?.(step)}>
                    <div
                        className={[
                            "h-[4px] w-full transition-all duration-300",
                            isCompleted ? "bg-custom-primary-default" : "bg-gray-400",
                        ].join(" ")}
                    />
                    <span
                        className={[
                            "text-[11px] flex justify-center",
                            isCompleted ? "text-custom-primary-default" : "text-gray-400",
                            isActive ? "font-semibold" : "font-normal",
                        ].join(" ")}
                    >
                        {label}
                    </span>
                </div>
            );
        })}
    </div>
);

/* ─── Main cards ──────────────────────────────────────────── */

const metrics = [
    { label: "Annual energy generation", value: "XX,XXX kWh/yr" },
    { label: "Total project cost", value: "₹XX,XXX Lakh" },
    { label: "Upfront payment after subsidy", value: "₹XX,XXX Lakh" },
    { label: "Payback period", value: "~4.2 years" },
];

const benefits = [
    "Detailed 3D roof and shadow analysis",
    "Complete design, approvals, and installation",
    `Best-price installer assigned by ${PVNXT_MODE}`,
    "End-to-end support until system is running",
];

export const ProposalCards = () => {
    const dispatch = useAppDispatch()
    const { roofAnalysis: { formDetails: { projectSetup }, selected_project } } = useAppSelector(state => state.consumerReducers)
    const { user: { country_mstr, userid } } = useAppSelector(state => state.auth);
    const modeContent = getInstallationModeContent(selected_project?.installationmode);

    return (
        <div className="grid grid-cols-2 bg-white gap-2 p-2">

            {/* ── Left card ── */}
            <div className="p-5 border border-gray-200 rounded-md">
                <p className="text-10-roboto uppercase mb-3.5">
                    Final Optimized Proposal
                </p>

                {/* System size */}
                <div className="flex gap-2 mb-1">
                    <span className="pt-2"><SolarPowerIcon /> </span>
                    <div>
                        <span className="heading-12-roboto">Final Plant Size (AC)</span>
                        <p className="text-xl font-semibold text-[rgba(17,63,74,1)] mb-1">{selected_project?.plant_ac_capacity ? selected_project?.plant_ac_capacity.toString() : ""}{` kW`}</p>
                    </div>
                </div>
                {/* Stats List */}
                <div className="border-t border-b border-gray-200 py-3.5 flex flex-col gap-3 mb-1">
                    <div className="flex justify-between items-center">
                        <span className="heading-12-inter">Annual energy generation</span>
                        <span className="heading-14-inter-info">{!selected_project?.project_generation_details?.pvnxt_generation ? `${formatEnergyGeneration(((+selected_project?.yearlygensanctioned!) || 0)?.toFixed(2))}` : `${formatEnergyGeneration(((+selected_project?.project_generation_details?.pvnxt_generation!) || 0)?.toFixed(2))}`}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="heading-12-inter">Total project cost</span>
                        <span className="heading-14-inter-info">{`${getCurrencyLabel(country_mstr)} ${formatMoney((((+selected_project?.projectcost!) || 0) / country_mstr?.currencyrate!), "tt")}`}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="heading-12-inter">Upfront payment after subsidy</span>
                        <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Upfront payment after subsidy")}`} className="text-[12px] font-medium underline text-[rgba(27,79,216,1)]" title={SUPPORT_EMAIL}>Connect with support team</a>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="heading-12-inter">Payback period</span>
                        <span className="heading-14-inter-info">{selected_project?.issubscribed ? `~ ${selected_project?.savingperiod ?? 0}` : `~ ${selected_project?.savingperiod ?? 0}`}{` years`}</span>
                    </div>
                </div>

                <div className="flex justify-between items-center mt-3">
                    <span className="text-[12px] font-inters text-black flex gap-2 pl-1">{<TimerIcon />}{`Installation completes in ${projectInstallationDays(selected_project)} days`}</span>
                    {/* <span className="text-custom-primary-dark font-medium font-inter text-[14px]">{"~₹XX.X Lakh"}</span> */}
                </div>

                {/* <div className="flex items-center gap-1.5 mt-4">
                <TimerIcon />
                <span className="text-xs text-black font-inter">Installation completes in 3–4 months</span>
            </div> */}

                <button className="mt-4 w-full py-2.5 border border-[#616161] rounded-md bg-white cursor-pointer flex items-center justify-center gap-1.5 text-sm font-medium text-[#000000] hover:bg-gray-50 transition-colors"
                    onClick={() => dispatch(setSeeProposal(true))}>
                    <ArticleIcon /> See Full Proposal
                </button>
            </div>

            {/* ── Right card ── */}
            <div className="p-5 border border-gray-200 rounded-md">
                <div className="flex justify-between items-start mb-1.5">
                    <p className="heading-20-inter-info">{modeContent.title}</p>
                    <span className="text-[11px] font-medium text-[#000000] border border-[#616161] rounded-full px-3 py-0.5 whitespace-nowrap">
                        Mode Selected
                    </span>
                </div>

                <p className="heading-14-inter-normal mb-3">{modeContent.tagline}</p>

                <p className="heading-16-inter-600 mb-3">{modeContent.benefitsHeading}</p>

                <div className="flex flex-col gap-4 mb-5">
                    {modeContent.benefits.map((b) => (
                        <div key={b} className="flex items-center gap-2">
                            <IconCheckCircle />
                            <span className="heading-12-inter-normal">{b}</span>
                        </div>
                    ))}
                </div>

                <p className="heading-12-inter-normal">{modeContent.footer}</p>
            </div>

        </div>
    );
}